import React, { useState } from 'react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from './ui/sheet';
import { Menu, Phone, LayoutDashboard, Mail } from 'lucide-react';
import { VeteranBadge } from './VeteranBadge';
import { useBooking } from '../context/BookingContext';

const navigation = [
  { label: 'Services', href: '#services' },
  { label: 'Maintenance Membership', href: '#maintenance-membership' },
  { label: 'Why Us', href: '#why-us' },
  { label: 'Careers', href: '#careers' }
];

// RemodWorks Logo Component
const RemodWorksLogo = ({ className = "" }) => (
  <div className={`flex items-start gap-0 ${className}`}>
    {/* Icon */}
    <LayoutDashboard className="w-6 h-6 text-orange-400 flex-shrink-0 mt-1" />
    
    {/* Text */}
    <div>
      <h1 className="text-2xl font-semibold text-white">
        <span className="bg-gradient-to-r from-orange-600 via-orange-700 to-amber-800 bg-clip-text text-transparent">Re</span>modWorks
      </h1>
      <p className="text-[10px] font-medium leading-tight text-slate-500">
        Home of Remoddash
      </p>
    </div>
  </div>
);

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { openBooking } = useBooking();

  const scrollToSection = (href) => {
    setIsOpen(false);
    setTimeout(() => {
      const element = document.querySelector(href);
      if (element) {
        const headerHeight = 64;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 400);
  };

  return (
    <header
      className="sticky top-0 z-40 bg-slate-950/80 backdrop-blur-xl border-b border-slate-800"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a href="#top">
              <RemodWorksLogo />
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navigation.map((item) => (
              <button
                key={item.label}
                onClick={() => scrollToSection(item.href)}
                className="text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center space-x-5">
            <button
              onClick={() => scrollToSection('#contact')}
              className="text-sm font-medium text-slate-300 hover:text-orange-400 transition-colors duration-200"
            >
              Contact Us
            </button>
            <Button
              data-testid="site-header-primary-cta-button"
              onClick={openBooking}
              className="btn-gradient-orange text-white h-11 px-5 rounded-lg shadow-lg transition-all duration-200 font-semibold"
            >
              Book Appointment
            </Button>
            <VeteranBadge size={65} idPrefix="header" />
          </div>

          {/* Mobile: Menu Button + Veteran Badge */}
          <div className="flex md:hidden items-center gap-2">
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-slate-800"
                  data-testid="site-header-mobile-menu-button"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
            <SheetContent side="right" className="w-[280px] sm:w-[350px] bg-slate-950 border-slate-800">
              <SheetTitle className="mt-8">
                <RemodWorksLogo />
              </SheetTitle>
              <SheetDescription className="sr-only">
                Navigation menu for RemodWorks home services website
              </SheetDescription>
              <div className="flex flex-col space-y-6 mt-6">
                <nav className="flex flex-col space-y-4">
                  {navigation.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => scrollToSection(item.href)}
                      className="text-left text-lg font-medium text-slate-300 hover:text-white transition-colors duration-200 py-2"
                    >
                      {item.label}
                    </button>
                  ))}
                </nav>
              </div>
              <div className="flex flex-col space-y-3 pt-6 border-t border-slate-800 mt-6">
                <a
                  href="tel:+12342051010"
                  className="inline-flex items-center gap-2 text-base font-medium text-slate-300 hover:text-white transition-colors duration-200"
                >
                  <Phone className="h-5 w-5" />
                  (234) 205-1010
                </a>
                <Button
                  onClick={openBooking}
                  className="btn-gradient-orange text-white h-12 px-6 rounded-lg shadow-lg transition-all duration-200 w-full font-semibold"
                >
                  Book Appointment
                </Button>
              </div>
            </SheetContent>
            </Sheet>
            <VeteranBadge size={65} idPrefix="header-mobile" />
          </div>
        </div>
      </div>
    </header>
  );
};
